import { Alert, Button, Card, Form, Input, Result, Typography } from 'antd'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { resetPassword } from '../api'
import type { SaasApiError } from '../api'

interface FormValues {
  password: string
  confirm: string
}

/**
 * Mounted at `/reset-password/:token` — the link inside the password
 * reset email (`{PUBLIC_ADMIN_BASE_URL}/reset-password/{token}`, see
 * app/core/email.py's build_password_reset_url). Password policy is
 * deliberately identical to SignUpPage's (`min: 8`, required) — reset
 * and signup must never silently diverge into two different password
 * rules; the backend re-enforces the exact same policy server-side
 * either way (app.saas.services.user_service.validate_password_policy).
 *
 * On success, redirects to `/login?password_reset=1` so LoginPage can
 * show a one-time "Password updated successfully" banner.
 */
export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tokenInvalid, setTokenInvalid] = useState(false)
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()

  const onFinish = async (values: FormValues) => {
    if (!token) return
    setSubmitting(true)
    setError(null)
    setTokenInvalid(false)
    try {
      await resetPassword(token, values.password, values.confirm)
      setSuccess(true)
    } catch (err) {
      const apiError = err as SaasApiError
      if (apiError.status === 404) {
        setTokenInvalid(true)
      } else {
        setError(apiError.message || 'Could not reset your password')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
      <Card style={{ width: 420 }}>
        {success ? (
          <Result
            status="success"
            title="Password reset successfully"
            subTitle="Your password has been updated. You can now sign in with your new password."
            extra={
              <Button type="primary" onClick={() => navigate('/login?password_reset=1', { replace: true })}>
                Sign in
              </Button>
            }
          />
        ) : tokenInvalid ? (
          <Result
            status="error"
            title="This link is invalid or has expired"
            subTitle="Password reset links expire after 30 minutes and can only be used once. Please request a new one."
            extra={
              <Link to="/forgot-password">
                <Button type="primary">Request a new link</Button>
              </Link>
            }
          />
        ) : (
          <>
            <Typography.Title level={3} style={{ marginBottom: 4 }}>
              Vistoera Cloud Admin
            </Typography.Title>
            <Typography.Title level={4} style={{ marginTop: 16, marginBottom: 4 }}>
              Create a new password
            </Typography.Title>
            <Form layout="vertical" onFinish={onFinish} style={{ marginTop: 24 }} disabled={submitting}>
              {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
              <Form.Item
                name="password"
                label="New password"
                rules={[
                  { required: true, message: 'Password is required' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password autoFocus autoComplete="new-password" placeholder="At least 8 characters" />
              </Form.Item>
              <Form.Item
                name="confirm"
                label="Confirm new password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve()
                      return Promise.reject(new Error('Passwords do not match'))
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" placeholder="Re-enter your new password" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 8 }}>
                <Button type="primary" htmlType="submit" block loading={submitting}>
                  Reset password
                </Button>
              </Form.Item>
              <Typography.Text type="secondary">
                <Link to="/login">Back to sign in</Link>
              </Typography.Text>
            </Form>
          </>
        )}
      </Card>
    </div>
  )
}
